import * as z from "zod/v4";

/**
 * Snapshot of ticket content at the time of validation.
 */
const TicketSnapshotSchema_ = z.object({
  title: z.string(),
  description: z.string().nullable(),
  comments: z.array(z.object({ author: z.string(), body: z.string() })),
  creatorName: z.string().nullable().optional(),
  linearStateName: z.string().nullable().optional(),
  labels: z.array(z.string()).optional(),
});

export const TicketSnapshotSchema: z.ZodType<TicketSnapshot> =
  TicketSnapshotSchema_;
export interface TicketSnapshot extends z.infer<typeof TicketSnapshotSchema_> {}

/**
 * A single validation attempt/comment on a ticket.
 */
const ValidationAttemptSchema_ = z.object({
  resultId: z.string(),
  isValid: z.boolean(),
  missingInformation: z.array(z.string()),
  reasoning: z.string(),
  extractedTeamId: z.string().nullable(),
  extractedMerchantName: z.string().nullable(),
  /** Ticket content at time of this validation */
  ticketSnapshot: TicketSnapshotSchema.optional(),
  createdAt: z.string(),
});

export const ValidationAttemptSchema: z.ZodType<ValidationAttempt> =
  ValidationAttemptSchema_;
export interface ValidationAttempt
  extends z.infer<typeof ValidationAttemptSchema_> {}

/**
 * A ticket with all its validation history.
 */
const TicketValidationResultSchema_ = z.object({
  linearTicketId: z.string(),
  linearTicketUrl: z.string(),
  linearTicketIdentifier: z.string(),
  /** Most recent validation status */
  currentStatus: z.enum(["approved", "rejected", "pending"]),
  /** Current status of the ticket in Linear (fetched live) */
  currentLinearStatus: z.string().nullable(),
  /** Current labels on the ticket in Linear (fetched live) */
  currentLinearLabels: z.array(z.string()).nullable(),
  /** Priority level (0=none, 1=urgent, 2=high, 3=medium, 4=low) */
  priority: z.number().nullable().optional(),
  /** Human-readable priority label */
  priorityLabel: z.string().nullable().optional(),
  /** When the SLA will be breached (ISO date string) */
  slaBreachesAt: z.string().nullable().optional(),
  /** When the SLA timer started (ISO date string) */
  slaStartedAt: z.string().nullable().optional(),
  /** Manual due date override (YYYY-MM-DD) */
  dueDate: z.string().nullable().optional(),
  /** Most recent merchant info extracted */
  extractedTeamId: z.string().nullable(),
  extractedMerchantName: z.string().nullable(),
  /** Number of validation attempts */
  attemptCount: z.number(),
  /** When the ticket was first validated */
  firstValidatedAt: z.string(),
  /** When the ticket was last validated */
  lastValidatedAt: z.string(),
  /** All validation attempts in chronological order (oldest first) */
  history: z.array(ValidationAttemptSchema),
  /** Whether this ticket was marked as "not a bug" (bug label removed) */
  markedAsNotBug: z.boolean().optional(),
  /** When the ticket was marked as "not a bug" */
  markedAsNotBugAt: z.string().nullable().optional(),
  /** Labels on the ticket when it was marked as not a bug (e.g., "Polish", "DevBug") */
  notBugLabels: z.array(z.string()).nullable().optional(),
  /** Manual status override (visual only) */
  statusOverride: z.enum(["approved", "rejected"]).optional(),
  /** When the status was manually overridden */
  statusOverrideAt: z.string().nullable().optional(),
  /** Who manually overrode the status */
  statusOverrideBy: z.string().nullable().optional(),
  /** Whether this ticket needs context (visual only, persisted) */
  needsContext: z.boolean().optional(),
  /** Whether this ticket was auto-expired after 3 days in Triaged Blocked + rejected */
  markedAsExpired: z.boolean().optional(),
  /** When the ticket was auto-expired */
  markedAsExpiredAt: z.string().nullable().optional(),
  /** Linear team key (e.g., "RETCO", "RET1", "RET2", "RET3") */
  teamKey: z.string().nullable().optional(),
  /** Linear team name */
  teamName: z.string().nullable().optional(),
  /** When the Linear ticket was created (ISO date string) */
  linearCreatedAt: z.string().nullable().optional(),
});

export const TicketValidationResultSchema: z.ZodType<TicketValidationResult> =
  TicketValidationResultSchema_;
export interface TicketValidationResult
  extends z.infer<typeof TicketValidationResultSchema_> {}

export const getTicketValidationResults = {
  name: "getTicketValidationResults" as const,
  input: z.object({
    page: z.number().int().positive().optional(),
    pageSize: z.number().int().positive().optional(),
    statusFilter: z.enum(["approved", "rejected", "pending"]).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    /** If true, only return tickets marked as "not a bug". If false/undefined, exclude them. */
    includeNotBug: z.boolean().optional(),
    /** If true, only return expired tickets. If false, exclude them. If undefined, include all. */
    includeExpired: z.boolean().optional(),
  }),
  output: z.object({
    results: z.array(TicketValidationResultSchema),
    total: z.number(),
  }),
};
